import isAppWithMock from 'src/utils/isAppWithMock';
import { mockUserDetailsList } from '../../__mocks__/users/userDetailListMock';
import {
  TAttendanceByDateResponse,
  TAttendanceByUserResponse,
  ICreateAttendanceRequest,
  normalizeAttendanceByDateResponseBody,
} from '../__shared/attendance.interfaces';
import { normalizePaginatedResponse } from '../__shared/paginationUtils';
import { TPaginationParams } from '../__shared/shared.interfaces';
import { mockAttendanceByDateEntries } from '../../__mocks__/__common/attendanceByDate.mock';
import { mockAttendanceByUser } from '../../__mocks__/__common/attendancebyUser.mock';
import {
  TStudentDetails,
  TStudentListApiResponse,
  TStudentPrimary,
  IStudentListQueryParams,
  ICreateStudentRequest,
  IUpdateStudentRequest,
  TStudentFeePrimary,
  IStudentFeesQueryParams,
  IUpdateFeePaymentRequest,
  IAssessmentListQueryParams,
  ICreateAssessmentRequest,
  IUpdateAssessmentRequest,
  TStudentDetailsApiResponse,
  TGuardianDetailsApiResponse,
  TStudentFeesApiResponse,
  IUpdateDraftWithFeeDetailsRequest,
  ICreateStudentWithBasicDetailsRequest,
  ICreateStudentWithBasicDetailsResponse,
  IPatchStudentBasicDetailsRequest,
  IPatchStudentBasicDetailsResponse,
  IUpdateDraftWithGuardianDetailsRequest,
  IUpdateDraftWithGradeDetailsRequest,
} from './students.interfaces';
import { createHttpService } from '../ApiService';
import { mockStudentDetails } from '../../__mocks__/students/studentDetailsMock';
import { studentAssessmentSubjectBasedMock } from 'src/__mocks__/students/studentAssessmentSubjectBasedMock';
import {
  IStudentAssessmentListApiResponse,
  IStudentSubjectAssessment,
  IStudentSubjectBasedAssessment,
  IStudentSubjectBasedAssessmentListApiResponse,
} from './studentsAssessment.interfaces';
import { studentAssessmentSubjectBasedStudentListMock } from 'src/__mocks__/students/studentAssessmentSubjectBasedStudentListMock';
import { TUserDetails, GENDER } from '../users/users.interfaces';
import { TDocument } from '../documents/documents.interfaces';
import { GRADE, SECTION } from '../admissions/admissions.interfaces';
import { TFeeComponent } from '../admissions/adminssionFees.interfaces';
import { FEE_PAYMENT_SCHEDULE, TStatus } from '../finance/finance.interfaces';

// ============================================================
// Response Mappers
// Transform API responses to frontend interface format
// ============================================================

/**
 * Maps guardian API response to frontend TUserDetails format
 */
const mapGuardianResponseToUserDetails = (
  response: TGuardianDetailsApiResponse
): TUserDetails => {
  return {
    id: response.userId, // API uses 'userId', map to 'id'
    basicDetails: {
      id: response.userId,
      firstName: response.basicDetails.firstName,
      middleName: response.basicDetails.middleName || null,
      lastName: response.basicDetails.lastName,
      gender: response.basicDetails.gender as GENDER,
    },
    contactDetails: {
      phone: {
        countryCode: response.contactDetails.phone.countryCode,
        phoneNumber: response.contactDetails.phone.phoneNumber,
        isVerified: response.contactDetails.phone.isVerified,
      },
      email: {
        email: response.contactDetails.email,
        isVerified: true, // API doesn't provide, default to true
      },
      address: {
        address1: response.contactDetails.address.address, // API uses 'address', map to 'address1'
        address2: null,
        city: response.contactDetails.address.city,
        state: response.contactDetails.address.state || null,
        country: response.contactDetails.address.country || null,
        postalCode: response.contactDetails.address.postalCode,
        isCurrent: true,
        description: null,
      },
    },
    documents: [], // API doesn't provide guardian documents in student response
    actions: {
      created: {
        userId: response.userId,
        timestamp: Date.now(),
      },
    },
  };
};

/**
 * Maps student details API response to frontend TStudentDetails format
 */
const mapStudentDetailsResponse = (
  response: TStudentDetailsApiResponse
): TStudentDetails => {
  // Map documents
  const documents: TDocument[] = response.documents.map((doc) => ({
    documentId: doc.documentId,
    userId: response.id, // Add userId from student
    documentName: doc.documentName,
    documentType: doc.documentType as any, // API uses string, cast to TDocumentType
    fileUrl: doc.fileUrl,
    status: doc.status as any, // API uses string, cast to TDocumentStatus
    submissionDate: doc.submissionDate, // Add submissionDate
    uploadedAt: doc.uploadedAt,
    description: doc.description,
  }));

  // Map guardians
  const guardianDetails: TUserDetails[] = response.guardianDetails.map(
    mapGuardianResponseToUserDetails
  );

  // Map fee components
  const feeComponents: TFeeComponent[] = response.feeDetails.feeComponents.map(
    (fee) => ({
      id: `fee-${fee.purpose}-${fee.academicYear}`,
      description: `${fee.componentName} for academic year ${fee.academicYear}`,
      status: fee.status as TStatus,
      componentId: fee.purpose,
      amount: fee.amount,
      paidAmount: fee.paidAmount,
      componentName: fee.componentName,
      dueDate: fee.dueDate.toString(),
      academicYear: fee.academicYear,
      purpose: fee.purpose,
      instituteRef: fee.instituteRef,
      createdBy: fee.createdBy,
    })
  );

  return {
    id: response.id,
    basicDetails: {
      id: response.id,
      firstName: response.studentBasicDetails.firstName,
      middleName: response.studentBasicDetails.middleName || null,
      lastName: response.studentBasicDetails.lastName,
      gender: response.studentBasicDetails.gender as GENDER,
    },
    contactDetails: {
      phone: {
        countryCode: response.studentContactDetails.phone.countryCode,
        phoneNumber: response.studentContactDetails.phone.phoneNumber,
        isVerified: response.studentContactDetails.phone.isVerified,
      },
      email: {
        email: response.studentContactDetails.email,
        isVerified: true,
      },
      address: {
        address1: response.studentContactDetails.address.address,
        address2: null,
        city: response.studentContactDetails.address.city,
        state: response.studentContactDetails.address.state || null,
        country: response.studentContactDetails.address.country || null,
        postalCode: response.studentContactDetails.address.postalCode,
        isCurrent: true,
        description: null,
      },
    },
    documents,
    actions: {
      created: {
        userId: response.actions.created.userId,
        timestamp: response.actions.created.timestamp,
      },
    },
    admissionDate: {
      userId: response.admissionDate.userId,
      timestamp: response.admissionDate.timestamp,
    },
    grade: response.grade as GRADE,
    gradeId: response.gradeId,
    section: response.section as SECTION,
    category: response.category,
    bankDetails: response.bankDetails,
    guardianDetails,
    feeDetails: {
      studentId: response.feeDetails.studentId,
      userId: response.feeDetails.userId,
      instituteId: response.feeDetails.instituteId,
      feeComponentId: '', // API doesn't provide
      feePaymentSchedule: FEE_PAYMENT_SCHEDULE.MONTHLY, // Default
      feeComponents,
      createdBy: response.feeDetails.createdBy,
    },
  };
};

/**
 * Maps student fees API response to frontend TStudentFeePrimary format
 */
const mapFeesResponse = (
  response: TStudentFeesApiResponse,
  studentId: string
): TStudentFeePrimary => {
  return {
    studentId,
    userId: '',
    instituteId: '',
    feeComponents: response.data.map((fee) => ({
      totalFee: fee.amount,
      status: fee.status,
      amount: fee.amount,
      paidAmount: fee.paidAmount,
      componentName: fee.componentName,
      dueDate: new Date(fee.dueDate),
      academicYear: fee.academicYear,
      purpose: fee.purpose,
      instituteRef: '',
      createdBy: '',
    })),
    createdBy: '',
  };
};

export const useStudentsService = () => {
  const httpAPI = createHttpService();
  const baseStudentPath = '';
  const withMockData = isAppWithMock();
  // const subBaseEndPoint = "/attendance";

  const getStudentList = (
    _params: TPaginationParams & Partial<IStudentListQueryParams>
  ): Promise<TStudentListApiResponse> => {
    if (withMockData) {
      // Filter mock data based on query parameters (mimicking API behavior)
      let filteredData = [...mockUserDetailsList];

      // Mirrors API: classId / sectionId (mock filter keys match `grade` / `section` on mock rows)
      if (_params.classId) {
        filteredData = filteredData.filter((student) => student.grade === _params.classId);
      }

      if (_params.sectionId) {
        filteredData = filteredData.filter((student) => student.section === _params.sectionId);
      }

      // Apply pagination
      const page = _params.page || 1;
      const limit = _params.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      return Promise.resolve(
        normalizePaginatedResponse<TStudentPrimary>(
          { data: paginatedData, count: filteredData.length, page, limit },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
        ...(_params.classId && { classId: _params.classId }),
        ...(_params.sectionId && { sectionId: _params.sectionId }),
        ...(_params.status && { status: _params.status }),
        ...(_params.academicYear != null && {
          academicYear: _params.academicYear,
        }),
        ...(_params.search && { search: _params.search }),
      },
    };

    return httpAPI.students
      .get<any>(`${baseStudentPath}`, requestObj)
      .then((res) => {
        return normalizePaginatedResponse<TStudentPrimary>(res ?? {}, 'data');
      })
      .catch((error) => {
        console.error('Error fetching student list:', error);
        return normalizePaginatedResponse<TStudentPrimary>({}, 'data');
      });
  };

  const getStudentAttendanceByDate = (
    _params: TPaginationParams,
    classId?: string,
    sectionId?: string,
    fromDate?: Date,
    toDate?: Date
  ): Promise<TAttendanceByDateResponse> => {
    if (withMockData) {
      return Promise.resolve(mockAttendanceByDateEntries);
    }
    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
        ...(classId && { classId }),
        ...(sectionId && { sectionId }),
        ...(fromDate && { fromDate: fromDate.toISOString() }),
        ...(toDate && { toDate: toDate.toISOString() }),
      },
    };
    return httpAPI.students
      .get<any>('/attendance/by-date', requestObj)
      .then((response) => {
        const body = response.data ?? response;
        return normalizeAttendanceByDateResponseBody(body);
      })
      .catch(() => {
        return [] as TAttendanceByDateResponse;
      });
  };

  const getStudentAttendanceByUser = (
    _params: TPaginationParams,
    classId?: string,
    sectionId?: string,
    fromDate?: Date,
    toDate?: Date,
    studentId?: string
  ): Promise<TAttendanceByUserResponse> => {
    if (withMockData) {
      const userId = mockAttendanceByUser.userDetails.id || 'user-1';
      return Promise.resolve({
        [userId]: mockAttendanceByUser,
      });
    }
    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
        ...(classId && { classId }),
        ...(sectionId && { sectionId }),
        ...(fromDate && { fromDate: fromDate.toISOString() }),
        ...(toDate && { toDate: toDate.toISOString() }),
        ...(studentId && { studentId }),
      },
    };
    return httpAPI.students
      .get<any>('/attendance/by-user', requestObj)
      .then((response) => {
        return (response.data || response) as TAttendanceByUserResponse;
      })
      .catch(() => {
        return {} as TAttendanceByUserResponse;
      });
  };

  // Legacy method for backward compatibility
  const getStudentAttandance = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _params: TPaginationParams,
    classId?: string,
    sectionId?: string,
    fromDate?: Date,
    toDate?: Date
  ): Promise<TAttendanceByDateResponse> => {
    if (withMockData) {
      return Promise.resolve(mockAttendanceByDateEntries);
    }
    return getStudentAttendanceByDate(_params, classId, sectionId, fromDate, toDate);
  };

  const getStudentDetailsbyId = (id: string): Promise<TStudentDetails> => {
    if (withMockData) {
      return Promise.resolve(mockStudentDetails);
    }

    return httpAPI.students
      .get<TStudentDetailsApiResponse>(`/details/${id}`)
      .then((response) => mapStudentDetailsResponse(response))
      .catch((error) => {
        console.error(`Error fetching student ${id}:`, error);
        return mockStudentDetails;
      });
  };

  // TODO: Naman > Wire selectedComponents from AdmissionStep4.Fees to create/update when backend supports UpdateDraftWithFeeDetailsRequest
  const createStudent = (
    data: ICreateStudentRequest
  ): Promise<TStudentDetails> => {
    if (withMockData) {
      return Promise.resolve({
        ...mockStudentDetails,
        ...data,
        id: 'stu_' + Date.now(),
      });
    }

    return httpAPI.students
      .post<TStudentDetails>(`${baseStudentPath}`, data)
      .then((response) => response)
      .catch((error) => {
        console.error('Error creating student:', error);
        throw error;
      });
  };

  const updateStudent = (
    id: string,
    data: IUpdateStudentRequest
  ): Promise<TStudentDetails> => {
    if (withMockData) {
      return Promise.resolve({
        ...mockStudentDetails,
        ...data,
        id,
      });
    }

    return httpAPI.students
      .put<TStudentDetails>(`/${id}`, data)
      .then((response) => response)
      .catch((error) => {
        console.error(`Error updating student ${id}:`, error);
        throw error;
      });
  };

  const deleteStudent = (id: string): Promise<void> => {
    if (withMockData) {
      return Promise.resolve();
    }

    return httpAPI.students
      .delete(`/${id}`)
      .then(() => undefined)
      .catch((error) => {
        console.error(`Error deleting student ${id}:`, error);
        throw error;
      });
  };

  const getStudentSubjectBasedAssessmentList = (
    _params: TPaginationParams & Partial<IAssessmentListQueryParams>
  ): Promise<IStudentSubjectBasedAssessmentListApiResponse> => {
    if (withMockData) {
      return Promise.resolve(
        normalizePaginatedResponse<IStudentSubjectBasedAssessment>(
          {
            data: studentAssessmentSubjectBasedMock,
            count: studentAssessmentSubjectBasedMock.length,
            page: _params.page ?? 1,
            limit: _params.limit ?? 10,
          },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
        ...(_params.classId && { classId: _params.classId }),
        ...(_params.subjectId && { subjectId: _params.subjectId }),
        ...(_params.academicYear && { academicYear: _params.academicYear }),
      },
    };

    return httpAPI.students
      .get<Record<string, unknown>>('/assessments/by-subject', requestObj)
      .then((res) =>
        normalizePaginatedResponse<IStudentSubjectBasedAssessment>(
          (res as Record<string, unknown>) ?? {},
          'data'
        )
      )
      .catch((error) => {
        console.error('Error fetching subject-based assessments:', error);
        throw error;
      });
  };

  const getStudentSubjectBasedStudentAssessmentList = (
    id: string
  ): Promise<IStudentAssessmentListApiResponse> => {
    if (withMockData) {
      return Promise.resolve({
        data: studentAssessmentSubjectBasedStudentListMock,
        total: studentAssessmentSubjectBasedStudentListMock.assessments.length,
      });
    }

    return httpAPI.students
      .get<any>(`/assessments/by-subject/${id}`)
      .then((response) => ({
        data: response.data || response,
        total: response.total ?? response.count ?? 0,
      }))
      .catch((error) => {
        console.error(`Error fetching student assessments for ${id}:`, error);
        return {
          data: studentAssessmentSubjectBasedStudentListMock,
          total: 0,
        };
      });
  };

  const addStudentSubjectBasedStudentAssessment = (
    assessment: IStudentSubjectAssessment | ICreateAssessmentRequest
  ): Promise<IStudentSubjectAssessment> => {
    if (withMockData) {
      return Promise.resolve(studentAssessmentSubjectBasedStudentListMock);
    }

    // Support both old IStudentSubjectAssessment format and new ICreateAssessmentRequest format
    const requestData = 'assessments' in assessment
      ? assessment // Old format - pass as is for backward compatibility
      : assessment; // New format - ICreateAssessmentRequest

    return httpAPI.students
      .post<IStudentSubjectAssessment>('/assessments', requestData)
      .then((response) => response)
      .catch((error) => {
        console.error('Error creating assessment:', error);
        throw error;
      });
  };

  const patchStudentSubjectBasedStudentAssessment = (
    assessmentOrId: IStudentSubjectAssessment | string,
    updateData?: IUpdateAssessmentRequest
  ): Promise<IStudentSubjectAssessment> => {
    if (withMockData) {
      return Promise.resolve(studentAssessmentSubjectBasedStudentListMock);
    }

    // Support both old signature (single IStudentSubjectAssessment) and new signature (id, data)
    let assessmentId: string;
    let requestData: any;

    if (typeof assessmentOrId === 'string') {
      // New format: separate id and data parameters
      assessmentId = assessmentOrId;
      requestData = updateData;
    } else {
      // Old format: IStudentSubjectAssessment object
      assessmentId = assessmentOrId.id;
      requestData = assessmentOrId;
    }

    return httpAPI.students
      .put<IStudentSubjectAssessment>(`/assessments/${assessmentId}`, requestData)
      .then((response) => response)
      .catch((error) => {
        console.error(`Error updating assessment ${assessmentId}:`, error);
        throw error;
      });
  };

  const getStudentFees = (
    studentId: string,
    params?: IStudentFeesQueryParams
  ): Promise<TStudentFeePrimary> => {
    if (withMockData) {
      return Promise.resolve({
        studentId,
        userId: 'user_' + studentId,
        instituteId: 'inst_001',
        feeComponents: [],
        createdBy: 'admin_001',
      });
    }

    const requestObj = {
      params: {
        ...(params?.academicYear && { academicYear: params.academicYear }),
        ...(params?.status && { status: params.status }),
      },
    };

    return httpAPI.students
      .get<TStudentFeesApiResponse>(`/${studentId}/fees`, requestObj)
      .then((response) => mapFeesResponse(response, studentId))
      .catch((error) => {
        console.error(`Error fetching fees for student ${studentId}:`, error);
        throw error;
      });
  };

  const updateStudentFeePayment = (
    studentId: string,
    feeId: string,
    data: IUpdateFeePaymentRequest
  ): Promise<any> => {
    if (withMockData) {
      return Promise.resolve({
        success: true,
        message: 'Payment updated successfully',
      });
    }

    return httpAPI.students
      .put<any>(`/${studentId}/fees/${feeId}`, data)
      .then((response) => response)
      .catch((error) => {
        console.error(
          `Error updating fee payment for student ${studentId}, fee ${feeId}:`,
          error
        );
        throw error;
      });
  };

  const postStudentAttendance = (
    attendance: ICreateAttendanceRequest
  ): Promise<{ message: string; date: string; recordsCount: number }> => {
    if (withMockData) {
      return Promise.resolve({
        message: `Attendance recorded for ${attendance.records.length} students`,
        date: attendance.date,
        recordsCount: attendance.records.length,
      });
    }

    return httpAPI.students
      .post<{ message: string; date: string; recordsCount: number }>('/attendance', attendance)
      .then((response) => response)
      .catch((error) => {
        console.error('Error posting attendance:', error);
        throw error;
      });
  };

  const updateStudentFeeDetails = (
    request: IUpdateDraftWithFeeDetailsRequest
  ): Promise<{ studentId: string; success: boolean; message: string }> => {
    if (withMockData) {
      return Promise.resolve({
        studentId: request.studentId,
        success: true,
        message: 'Fee details updated successfully',
      });
    }

    return httpAPI.students
      .put<{ studentId: string; success: boolean; message: string }>('/fee-details', request)
      .then((response) => response)
      .catch((error) => {
        console.error('Error updating fee details:', error);
        throw error;
      });
  };

  const createStudentWithBasicDetails = (
    body: ICreateStudentWithBasicDetailsRequest
  ): Promise<ICreateStudentWithBasicDetailsResponse> => {
    if (withMockData) {
      return Promise.resolve({
        studentId: 'stu_mock_' + Date.now(),
        userId: 'usr_mock_' + Date.now(),
        success: true,
        message: 'Created',
      });
    }
    return httpAPI.students
      .post<ICreateStudentWithBasicDetailsResponse>('/basic-details', body)
      .then((response) => response)
      .catch((error) => {
        console.error('Error creating student basic details:', error);
        throw error;
      });
  };

  const patchStudentBasicDetails = (
    body: IPatchStudentBasicDetailsRequest
  ): Promise<IPatchStudentBasicDetailsResponse> => {
    if (withMockData) {
      return Promise.resolve({
        studentId: body.studentId,
        userId: body.userId ?? 'usr_mock',
        success: true,
        message: 'Updated',
      });
    }
    return httpAPI.students
      .patch<IPatchStudentBasicDetailsResponse>('/basic-details', body)
      .then((response) => response)
      .catch((error) => {
        console.error('Error updating student basic details:', error);
        throw error;
      });
  };

  const updateStudentGuardianDetails = (
    body: IUpdateDraftWithGuardianDetailsRequest
  ): Promise<{ studentId: string; userId: string; guardianId: string; success: boolean; message: string }> => {
    if (withMockData) {
      return Promise.resolve({
        studentId: body.studentId,
        userId: body.userId,
        guardianId: 'grd_mock',
        success: true,
        message: 'OK',
      });
    }
    return httpAPI.students
      .put('/guardian-details', body)
      .then((response) => response as { studentId: string; userId: string; guardianId: string; success: boolean; message: string })
      .catch((error) => {
        console.error('Error updating guardian details:', error);
        throw error;
      });
  };

  const updateStudentGradeDetails = (
    body: IUpdateDraftWithGradeDetailsRequest
  ): Promise<{ studentId: string; userId: string; sectionId: string; success: boolean; message: string }> => {
    if (withMockData) {
      return Promise.resolve({
        studentId: body.studentId,
        userId: body.userId,
        sectionId: body.sectionId,
        success: true,
        message: 'OK',
      });
    }
    return httpAPI.students
      .put('/grade-details', body)
      .then((response) => response as { studentId: string; userId: string; sectionId: string; success: boolean; message: string })
      .catch((error) => {
        console.error('Error updating grade details:', error);
        throw error;
      });
  };

  return {
    // Students CRUD
    getStudentList,
    getStudentDetailsbyId,
    createStudent,
    updateStudent,
    deleteStudent,

    // Attendance
    getStudentAttandance,
    getStudentAttendanceByDate,
    getStudentAttendanceByUser,
    postStudentAttendance,

    // Assessments
    getStudentSubjectBasedAssessmentList,
    getStudentSubjectBasedStudentAssessmentList,
    addStudentSubjectBasedStudentAssessment,
    patchStudentSubjectBasedStudentAssessment,

    // Student Fees
    getStudentFees,
    updateStudentFeePayment,
    updateStudentFeeDetails,
    createStudentWithBasicDetails,
    patchStudentBasicDetails,
    updateStudentGuardianDetails,
    updateStudentGradeDetails,
  };
};
